// MapMark — full PinForm flow live (Day 8).
// Tap map → pending pin → PinForm → confirm → setDoc → cleared.
// All writes use placeMarker() which sets {userId_regionId} deterministic id.
//
// Day 14 (2026-05-25): map goes full-screen. Region picker chips float
// top-left, capacity counter top-right, PinForm panel docks above AdminNav
// as a translucent overlay so the map remains the hero throughout.

import { FieldMap, Han, MonoLabel, T } from "@goodbye/ui";
import { useEffect, useMemo, useRef, useState } from "react";
import { useAdminAuthCtx, useAdminSyncCtx } from "../context";
import { useEventConfig } from "../hooks/useEventConfig";
import { useAdminGeolocation } from "../hooks/useAdminGeolocation";
import { useDeviceHeading } from "../hooks/useDeviceHeading";
import { usePersistentState } from "../hooks/usePersistentState";
import { env } from "../env";
import { movePin, placePin, removePin, removePinBatch } from "../lib/placeMarker";
import { REWARD_REGION_IDS } from "../lib/rewardRegions";
import { uploadPinPhotos } from "../lib/uploadPinPhotos";
import { PinEditPanel } from "./_PinEditPanel";
import { PinForm } from "./_PinForm";

// 2026-05-30:學員位置點的「多久前回報」相對時間。atMs=null(無時間戳)→
// 空字串。≤10s →「剛剛」;<60s →「N 秒前」;<60m →「N 分前」;否則「N 時前」。
// now 由呼叫端的 ticking state 帶入,讓標籤會自己跳動。
function agoLabel(atMs: number | null, now: number): string {
  if (atMs == null) return "";
  const sec = Math.max(0, Math.round((now - atMs) / 1000));
  if (sec <= 10) return "剛剛";
  if (sec < 60) return `${sec} 秒前`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} 分前`;
  const hr = Math.round(min / 60);
  return `${hr} 時前`;
}

// Pin edit state machine. Lives alongside the existing `pending` state
// which handles "tapping empty map to place a NEW pin".
//   null                  → idle (taps on empty map → pending)
//   {phase: "viewing"}    → tapped an existing pin, panel shows actions
//   {phase: "awaiting…"}  → user picked 更改位置, next map tap = new lat/lng
//   {phase: "confirming"} → got the new lat/lng, awaiting confirm
//   {phase: "removing"}   → delete in-flight
type EditingState =
  | null
  | {
      phase: "viewing" | "awaiting-new-location" | "confirming-move" | "removing";
      markerId: string;
      pinId: string;
      pendingMove: { lat: number; lng: number } | null;
    };

// Default region the worker lands on when opening MapMark — ruifang is
// the activity's main hide site, so we surface it first. Other regions
// (taipei / wangyou / haikeguan / sandiaoling) are always writable in
// the worker UI regardless of `open` flag — `open` only controls learner
// visibility. Closed regions in the picker are labelled with · 關.
const DEFAULT_REGION_ID = "ruifang";

export function MapMark() {
  const { email } = useAdminAuthCtx();
  const { regions, markers, users, loading } = useAdminSyncCtx();
  const { cohortId: currentCohortId } = useEventConfig();
  const geo = useAdminGeolocation(true);
  // 2026-05-30: workers also want to see their facing direction (compass cone)
  // like the learner map. iOS gates DeviceOrientation behind a user gesture —
  // the locate-button tap (onRecenterTap) requests it. Same hook as user app.
  const { heading, permission: headingPermission, request: requestHeadingPermission } =
    useDeviceHeading();
  const openRegions = useMemo(() => regions.filter((r) => r.open), [regions]);

  // 2026-05-30:idle 面板的操作說明已整批移除(工作員都熟了),原本的
  // helpDismissed / dismissHelp / HELP_DISMISSED_KEY 一併拿掉。

  // 2026-05-30:篩選設定切 tab 回來要記得(MapMark 換 route 會卸載 →
  // useState 歸零)。以下 6 個用 usePersistentState 存 localStorage。
  const [activeRegionId, setActiveRegionId] = usePersistentState<string | null>(
    "admin-map-activeRegionId",
    null,
  );

  // Stage 11: 第一次 GPS fix 到、若使用者落在某 region 的 bounds 內、自動切過去。
  // 只跑一次(ref guard)、避免 worker 手動切過區後 GPS 抖動又被拉回。
  // 不在任何 region bounds 內(辦公室、家裡)→ 不動、維持 fallback ruifang。
  // 工作員地圖用 regions 全集(open + closed)、不像學員受 open 限制
  // (Stage 9.5 已確定:`open` 只控制學員可見性、工作員無論開關都進得去)。
  const autoRegionAppliedRef = useRef(false);
  useEffect(() => {
    if (autoRegionAppliedRef.current) return;
    if (!geo.coords) return;
    const coords = geo.coords;
    const inside = regions.find(
      (r) =>
        coords.lat >= r.boundsSw[0] &&
        coords.lat <= r.boundsNe[0] &&
        coords.lng >= r.boundsSw[1] &&
        coords.lng <= r.boundsNe[1],
    );
    if (inside) {
      autoRegionAppliedRef.current = true;
      setActiveRegionId(inside.id);
    }
  }, [geo.coords, regions]);

  const region =
    regions.find((r) => r.id === activeRegionId) ??
    regions.find((r) => r.id === DEFAULT_REGION_ID) ??
    openRegions[0] ??
    regions[0] ??
    null;

  const regionMarkerParents = region
    ? markers.filter((m) => m.regionId === region.id)
    : [];

  // Learner filter — null = show all (default). Set membership = show
  // only those learners. Mounted as a paper badge next to the region
  // picker. Editing flow still works on filtered markers; if a learner
  // is hidden the worker can either turn the filter off or use the
  // learners tab (UsersOverview) to find them.
  // null=全選 / Set(non-empty)=只顯示這些 / Set(empty)=全隱。Set 要序列化:
  // null→null、Set→array。
  const [selectedUserIds, setSelectedUserIds] = usePersistentState<Set<string> | null>(
    "admin-map-selectedUserIds",
    null,
    {
      serialize: (v) => (v === null ? null : Array.from(v)),
      deserialize: (raw) =>
        raw === null ? null : new Set(raw as string[]),
    },
  );
  const [filterOpen, setFilterOpen] = useState(false);

  // 梯次篩選 — "all" = 全部屆、其他值為 cohortId 字串(e.g. "2025"/"2026")。
  // 預設 "all" 以向下相容(只有一屆時等同全選)。切換梯次時重設學員勾選。
  const [mapCohortFilter, setMapCohortFilter] = usePersistentState<string>(
    "admin-cohortFilter",
    "all",
  );

  // 目前資料庫中出現的屆別(不含 worker)。
  const availableCohorts = useMemo(() => {
    const ids = new Set<string>();
    for (const u of users) {
      if (u.role !== "worker") ids.add(u.cohortId ?? "2025");
    }
    return Array.from(ids).sort();
  }, [users]);

  // 依梯次過濾後的學員(worker 已排除)。
  const cohortFilteredLearners = useMemo(
    () =>
      users.filter(
        (u) =>
          u.role !== "worker" &&
          (mapCohortFilter === "all" || (u.cohortId ?? "2025") === mapCohortFilter),
      ),
    [users, mapCohortFilter],
  );

  // "Show all placers" toggle — default OFF, so a worker only sees the
  // pins they themselves placed. Useful when 5+ workers are hiding in
  // the same region at the same time and visual noise on the map gets
  // overwhelming. Pin-level placedBy is the source of truth; legacy
  // pins without placedBy (pre-Day 13) treat as "always visible".
  const [showAllPlacers, setShowAllPlacers] = usePersistentState(
    "admin-map-showAllPlacers",
    false,
  );

  // 2026-05-30:「顯示學員」開關(左下角,預設關)。開 → 在地圖上疊出有回報
  // lastLocation、且在當前 region bounds 內的學員位置點。學員位置由學員端
  // 在地圖頁低頻(45s)回報寫進 /users.lastLocation。
  const [showLearners, setShowLearners] = usePersistentState(
    "admin-map-showLearners",
    false,
  );

  // 2026-05-30:每個學員點旁顯示「幾秒/分前回報」。需要一個會自己跳動的
  // now 才能讓相對時間更新(否則停在「1 分前」不動)。只在 showLearners 開
  // 時每 15s tick,避免沒在看時白白 re-render。
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!showLearners) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(id);
  }, [showLearners]);

  // 找到 / 未找到 狀態篩選 — 預設兩個都開。兩個都可勾,但不能兩個都關
  // (那等於整張地圖空白、沒意義)。取消其中一個時,若會導致兩個都 false,
  // 就自動把另一個補回 true,使用者體感比「擋住不給取消」順。2026-05-30。
  const [showFound, setShowFound] = usePersistentState("admin-map-showFound", true);
  const [showUnfound, setShowUnfound] = usePersistentState(
    "admin-map-showUnfound",
    true,
  );
  function toggleShowFound(next: boolean) {
    if (!next && !showUnfound) {
      // 取消「找到」會導致兩者皆關 → 自動開啟「未找到」。
      setShowFound(false);
      setShowUnfound(true);
    } else {
      setShowFound(next);
    }
  }
  function toggleShowUnfound(next: boolean) {
    if (!next && !showFound) {
      setShowUnfound(false);
      setShowFound(true);
    } else {
      setShowUnfound(next);
    }
  }

  // selectedUserIds 模型:
  //   null         → 全選(所有學員的 pin 都顯示)
  //   Set(non-empty) → 只顯示集合內的學員
  //   Set(empty)   → 全清(地圖不顯示任何 pin,等工作員從 14 人挑)
  // 全選跟清除是兩種不同的使用者意圖(audit 全場 vs 從零挑特定幾個)、
  // 不能用同一個 state 表示。
  const regionFieldMarkers = regionMarkerParents
    .filter((m) => selectedUserIds === null || selectedUserIds.has(m.userId))
    .flatMap((m) =>
      // Index from the FULL pins list so the learner-facing 1/2/3 numbering
      // stays stable regardless of placer / found filters.
      m.pins
        .map((p, idx) => ({ p, idx }))
        .filter(
          ({ p }) =>
            showAllPlacers ||
            !p.placedBy ||
            p.placedBy === email,
        )
        .filter(({ p }) => (p.foundAt != null ? showFound : showUnfound))
        .map(({ p, idx }) => ({
          id: `${m.id}__${p.id}`,
          userId: m.userId,
          regionId: m.regionId,
          lat: p.lat,
          lng: p.lng,
          pinIndex: idx + 1,
          found: p.foundAt != null,
        })),
    );

  // 2026-05-30:學員位置點(只在 showLearners 開 + 有 lastLocation + 在當前
  // region bounds 內 + 非 worker 帳號)。bounds 過濾在 client 做(14 人量級
  // 無壓力、Firestore 無原生 geo query)。
  const learnerDots =
    showLearners && region
      ? cohortFilteredLearners.flatMap((u) => {
          const loc = u.lastLocation;
          if (!loc || typeof loc.lat !== "number" || typeof loc.lng !== "number")
            return [];
          const [swLat, swLng] = region.boundsSw;
          const [neLat, neLng] = region.boundsNe;
          const inBounds =
            loc.lat >= swLat &&
            loc.lat <= neLat &&
            loc.lng >= swLng &&
            loc.lng <= neLng;
          if (!inBounds) return [];
          // lastLocation.at 是 Firestore Timestamp(有 toMillis);保險也接
          // 普通物件 / 缺值。算出「幾秒/分前」相對時間(用會跳動的 now)。
          const atMs =
            loc.at && typeof loc.at.toMillis === "function"
              ? loc.at.toMillis()
              : null;
          return [
            {
              userId: u.id,
              name: u.name,
              lat: loc.lat,
              lng: loc.lng,
              ago: agoLabel(atMs, now),
            },
          ];
        })
      : undefined;

  const [pending, setPending] = useState<{ lat: number; lng: number } | null>(null);
  const [writing, setWriting] = useState(false);
  // 上傳/轉檔階段文案(collectible pin 附影片時的「轉檔中…」進度)。
  const [writingPhase, setWritingPhase] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<EditingState>(null);
  // 「· 藏 一 個」主動作 state:focusPoint 餵給 FieldMap 觸發 panTo、
  // pinHereError 在 GPS 不在任何 open region 內時顯示給工作員。
  const [focusPoint, setFocusPoint] = useState<{ lat: number; lng: number } | null>(null);
  const [pinHereError, setPinHereError] = useState<string | null>(null);
  const filterCount =
    selectedUserIds === null ? cohortFilteredLearners.length : selectedUserIds.size;

  // 主按鈕計算:依 GPS 狀態 + accuracy 三段門檻決定 disabled、警告、提示。
  // - GPS=null / denied / unsupported / error → disabled、提示分支
  // - GPS coords 有但 accuracy > 80m → disabled(現場太爛、藏歪很多)
  // - 30-80m → 可按但顯示警告
  // - < 30m → 安靜直接 OK
  const pinHereDisabled =
    !geo.coords ||
    (geo.accuracy !== null && geo.accuracy > 80);
  const pinHereWarning =
    geo.coords && geo.accuracy !== null && geo.accuracy > 30 && geo.accuracy <= 80
      ? `· GPS 誤差 ${Math.round(geo.accuracy)}m、可能歪幾米`
      : null;
  const pinHereStatusHint =
    geo.status === "denied"
      ? "· 定位被拒、請開系統權限後重開"
      : geo.status === "unsupported"
        ? "· 此裝置不支援 GPS、請點地圖"
        : geo.status === "error"
          ? "· GPS 失敗、請點地圖"
          : !geo.coords
            ? "· 等 GPS"
            : geo.accuracy !== null && geo.accuracy > 80
              ? `· GPS 誤差 ${Math.round(geo.accuracy)}m、太大、請點地圖`
              : null;

  function handlePinHere() {
    if (!geo.coords) return;
    setPinHereError(null);
    // 找含 GPS 的 region(全集、不限 open)。工作員要能在還沒對學員
    // 開放的區先佈點 — 藏點與 learner 可見性脫鉤,open 只控制後者。
    // GPS 在多區交界時取第一個 hit、其他不處理(罕見、bounds 不重疊)。
    const inside = regions.find((r) => {
      const lat = geo.coords!.lat;
      const lng = geo.coords!.lng;
      return (
        lat >= r.boundsSw[0] &&
        lat <= r.boundsNe[0] &&
        lng >= r.boundsSw[1] &&
        lng <= r.boundsNe[1]
      );
    });
    if (!inside) {
      // 不彈 PinForm — 避免把 pin 寫到錯 region、可能在 maxBounds 外。
      setPinHereError("目前位置不在任何區域範圍內、請手動切區或直接點地圖");
      return;
    }
    if (inside.id !== region?.id) {
      setActiveRegionId(inside.id);
    }
    setEditing(null);
    setFocusPoint({ lat: geo.coords.lat, lng: geo.coords.lng });
    setPending({ lat: geo.coords.lat, lng: geo.coords.lng });
  }

  const toggleUser = (uid: string) => {
    setSelectedUserIds((prev) => {
      // Start from "all selected" if we were on null (show-all).
      const base = prev ?? new Set(users.map((u) => u.id));
      const next = new Set(base);
      if (next.has(uid)) next.delete(uid);
      else next.add(uid);
      // 允許空集合存在 — 工作員按「· 清 除」之後手動勾選的過程中
      // 會經過空集合狀態,不能 fallback 回 null(會 hijack 工作員意圖)。
      // If all are selected, normalise back to null so re-renders don't
      // create a new Set every tick.
      if (next.size === users.length) return null;
      return next;
    });
  };
  // Resolve the marker + pin currently being edited (or null).
  const editingMarker = editing
    ? regionMarkerParents.find((m) => m.id === editing.markerId) ?? null
    : null;

  async function handleConfirm({
    userIds,
    clue,
    photoFiles,
    pinKind,
    payloadText,
    payloadPhotoFiles,
    payloadVideoFile,
  }: {
    userIds: string[];
    clue: string;
    photoFiles: File[];
    pinKind: "reward" | "collectible" | "announcement";
    payloadText: string;
    payloadPhotoFiles: File[];
    payloadVideoFile: File | null;
  }) {
    if (!region || !pending || userIds.length === 0) return;
    setWriting(true);
    setError(null);
    try {
      const reward = pinKind === "reward";
      const collectible = pinKind === "collectible";
      // 2026-05-30 公告 pin:純資訊,學員端無收集按鈕。它跟 collectible 一樣
      // 帶 payload(文字/照片 = 公告內容),所以上傳流程共用。
      const announcement = pinKind === "announcement";
      const hasPayload = collectible || announcement;

      // ── Collectible payload: upload ONCE, shared across all 全員 pins ──
      // The same content (text/photos/video) is copied to every learner's
      // pin, so we only upload + transcode once and reuse the URLs. Uses a
      // single shared id (client ms) for the storage paths.
      let sharedPayload:
        | {
            payloadText: string;
            payloadPhotoUrls?: string[];
            payloadPhotoStoragePaths?: string[];
            payloadVideoUrl?: string;
            payloadVideoStoragePath?: string;
          }
        | null = null;
      if (hasPayload) {
        const shareId = `pinpayload-${Date.now()}`;
        let payloadPhotoUrls: string[] | undefined;
        let payloadPhotoStoragePaths: string[] | undefined;
        if (payloadPhotoFiles.length > 0) {
          setWritingPhase("上傳碎片照片中…");
          const { uploadFragmentPhotos } = await import(
            "../lib/uploadFragmentPhotos"
          );
          const res = await uploadFragmentPhotos({
            fragmentId: shareId,
            files: payloadPhotoFiles,
          });
          payloadPhotoUrls = res.photoUrls;
          payloadPhotoStoragePaths = res.photoStoragePaths;
        }
        let payloadVideoUrl: string | undefined;
        let payloadVideoStoragePath: string | undefined;
        if (payloadVideoFile) {
          setWritingPhase("上傳影片中…");
          const { uploadFragmentVideo } = await import(
            "../lib/uploadFragmentVideo"
          );
          const { pollTranscodedVideo } = await import(
            "../lib/pollTranscodedVideo"
          );
          await uploadFragmentVideo({
            fragmentId: shareId,
            file: payloadVideoFile,
          }).done;
          setWritingPhase("影片轉檔中…(請稍候)");
          payloadVideoUrl = await pollTranscodedVideo(shareId);
          payloadVideoStoragePath = `fragments-videos/${shareId}.mp4`;
        }
        sharedPayload = {
          payloadText,
          ...(payloadPhotoUrls ? { payloadPhotoUrls, payloadPhotoStoragePaths } : {}),
          ...(payloadVideoUrl ? { payloadVideoUrl, payloadVideoStoragePath } : {}),
        };
      }

      setWritingPhase(null);
      // 全員 picks N learners → loop placePin per id at the same lat/lng.
      // 多人藏點(N>1)時共用同一個 placedBatchAt(client ms timestamp)、
      // 後續可一次批刪。單人藏點不寫此欄位、保持 schema 清乾淨。
      const photoFailures: string[] = [];
      const placedBatchAt = userIds.length > 1 ? Date.now() : undefined;
      for (const userId of userIds) {
        const { pinId } = await placePin({
          userId,
          regionId: region.id,
          lat: pending.lat,
          lng: pending.lng,
          clue,
          placedBy: email ?? "unknown",
          placedBatchAt,
          reward,
          collectible,
          announcement,
        });

        const { pinDoc } = await import("@goodbye/firebase");
        const { updateDoc } = await import("firebase/firestore");

        // Collectible payload (shared URLs) → write to this pin.
        if (sharedPayload) {
          await updateDoc(pinDoc(userId, region.id, pinId), sharedPayload);
        }

        // Clue photos (find-the-spot hint) — per-pin upload, all kinds.
        if (photoFiles.length > 0) {
          try {
            const { photoUrls, photoStoragePaths } = await uploadPinPhotos({
              userId,
              regionId: region.id,
              pinId,
              files: photoFiles,
            });
            await updateDoc(pinDoc(userId, region.id, pinId), {
              photoUrls,
              photoStoragePaths,
            });
          } catch (uploadErr) {
            console.warn("[MapMark] photo upload failed", uploadErr);
            photoFailures.push(userId);
          }
        }
      }

      if (photoFailures.length > 0) {
        setError(
          `釘已藏${userIds.length > 1 ? `(${userIds.length} 位)` : ""},但 ${photoFailures.length} 個照片上傳失敗`,
        );
      }

      setPending(null);
      // 清 focusPoint;下次按主按鈕、GPS 即使沒移動,setFocusPoint(同
      // 座標)會建立新 object reference、useEffect 的 lat/lng 依賴雖然
      // 相同但因為先 null 再賦值、相當於「重置 → 重新觸發」、panTo 會
      // 再次跑。
      setFocusPoint(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
      setWritingPhase(null);
    }
  }

  /** Tap on an existing marker → open edit panel. Marker id has shape
   *  "{userId}_{regionId}__{pinId}" (see regionFieldMarkers above), so
   *  we split on "__" to recover the pin id; the prefix is the parent
   *  marker doc id. */
  function handleTapMarker(markerCompositeId: string) {
    // If we're in the middle of placing a new pin, ignore — the worker
    // can cancel that flow explicitly.
    if (pending) return;
    const [parentId, pinId] = markerCompositeId.split("__");
    if (!parentId || !pinId) return;
    setEditing({ phase: "viewing", markerId: parentId, pinId, pendingMove: null });
    setError(null);
  }

  /** "更改位置" pressed → wait for the next map tap to supply the new
   *  lat/lng. Map's onPlace handler below will pick it up. */
  function handleStartMove() {
    if (!editing) return;
    setEditing({ ...editing, phase: "awaiting-new-location", pendingMove: null });
  }

  /** Confirm the new location → call movePin, then close the panel. */
  async function handleConfirmMove() {
    if (!editing || !editingMarker || !editing.pendingMove || !region) return;
    setWriting(true);
    setError(null);
    try {
      await movePin({
        userId: editingMarker.userId,
        regionId: region.id,
        pinId: editing.pinId,
        lat: editing.pendingMove.lat,
        lng: editing.pendingMove.lng,
        movedBy: email ?? "unknown",
      });
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setWriting(false);
    }
  }

  /** Toggle the pin's 「藏照片」(reward) attribute on an existing pin.
   *  (2026-05-30: workers need to fix a pin's reward flag after placing —
   *  e.g. unchecked by mistake, or a non-photo pin should become one.) */
  async function handleToggleReward(next: boolean) {
    if (!editing || !editingMarker || !region) return;
    setError(null);
    try {
      const { pinDoc } = await import("@goodbye/firebase");
      const { updateDoc } = await import("firebase/firestore");
      await updateDoc(pinDoc(editingMarker.userId, region.id, editing.pinId), {
        reward: next,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  /** Append clue photos to an EXISTING pin (2026-05-30 multi-photo). Uploads
   *  to pins-photos/{marker}/{pinId}/{uuid}.jpg then arrayUnion-merges the
   *  new URLs/paths onto the pin doc — atomic, offline-safe, and creates the
   *  array on the first append to an old single-photo pin. Throws on failure
   *  so the panel can surface an inline error. */
  async function handleAddPhotos(files: File[]) {
    if (!editing || !editingMarker || !region || files.length === 0) return;
    const { photoUrls, photoStoragePaths } = await uploadPinPhotos({
      userId: editingMarker.userId,
      regionId: region.id,
      pinId: editing.pinId,
      files,
    });
    const { pinDoc } = await import("@goodbye/firebase");
    const { updateDoc, arrayUnion } = await import("firebase/firestore");
    await updateDoc(pinDoc(editingMarker.userId, region.id, editing.pinId), {
      photoUrls: arrayUnion(...photoUrls),
      photoStoragePaths: arrayUnion(...photoStoragePaths),
    });
  }

  /** 2026-05-30:移除既有 pin 上的「單張」提示照(工作員放錯 / 想換)。確認
   *  由 _PinEditPanel 的兩段式 × 處理,這裡只做真刪。做法:讀現值、用 index
   *  重算整個陣列再整包 set(不靠 arrayRemove 的值比對,避免重複 URL 誤刪),
   *  並同步刪掉對應 Storage 檔(fire-and-forget)。也處理 legacy 單張欄位。 */
  async function handleRemovePhoto(urlToRemove: string) {
    if (!editing || !editingMarker || !region) return;
    const pin = editingMarker.pins.find((p) => p.id === editing.pinId) as
      | {
          photoUrl?: string;
          photoStoragePath?: string;
          photoUrls?: string[];
          photoStoragePaths?: string[];
        }
      | undefined;
    if (!pin) return;

    const { pinDoc, getFirebase } = await import("@goodbye/firebase");
    const { updateDoc } = await import("firebase/firestore");
    const ref = pinDoc(editingMarker.userId, region.id, editing.pinId);

    let storagePathToDelete: string | undefined;

    if (Array.isArray(pin.photoUrls) && pin.photoUrls.includes(urlToRemove)) {
      // Multi-photo path: drop the URL + its same-index storage path.
      const idx = pin.photoUrls.indexOf(urlToRemove);
      storagePathToDelete = pin.photoStoragePaths?.[idx];
      const nextUrls = pin.photoUrls.filter((_, i) => i !== idx);
      const nextPaths = (pin.photoStoragePaths ?? []).filter((_, i) => i !== idx);
      await updateDoc(ref, {
        photoUrls: nextUrls,
        photoStoragePaths: nextPaths,
      });
    } else if (pin.photoUrl === urlToRemove) {
      // Legacy single-photo field.
      storagePathToDelete = pin.photoStoragePath;
      const { deleteField } = await import("firebase/firestore");
      await updateDoc(ref, {
        photoUrl: deleteField(),
        photoStoragePath: deleteField(),
      });
    } else {
      return; // URL not found on this pin — nothing to do.
    }

    // Delete the Storage object (non-fatal — a dead URL is harmless, learner
    // UI skips 404s; this just reclaims storage).
    if (storagePathToDelete) {
      const { ref: storageRef, deleteObject } = await import("firebase/storage");
      void deleteObject(
        storageRef(getFirebase().storage, storagePathToDelete),
      ).catch(() => {});
    }
  }

  /** "移除" → delete the pin doc. */
  async function handleRemovePin() {
    if (!editing || !editingMarker || !region) return;
    setEditing({ ...editing, phase: "removing" });
    setError(null);
    try {
      await removePin({
        userId: editingMarker.userId,
        regionId: region.id,
        pinId: editing.pinId,
      });
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      // Roll back to viewing so the worker can try again or cancel.
      setEditing((prev) =>
        prev ? { ...prev, phase: "viewing" } : prev,
      );
    }
  }

  /** "移除同批" → 跨所有學員找同 placedBatchAt 的 pin、一次刪光。
   *  只在被點的 pin 有 placedBatchAt 時可用。 */
  async function handleRemoveBatch() {
    if (!editing || !editingMarker || !region) return;
    const targetPin = editingMarker.pins.find((p) => p.id === editing.pinId);
    if (!targetPin?.placedBatchAt) return;
    const batchAt = targetPin.placedBatchAt;
    // 找所有 markers 內 placedBatchAt 同值的 pin。region 限定當前區。
    const toDelete: { userId: string; regionId: string; pinId: string }[] = [];
    for (const m of regionMarkerParents) {
      for (const p of m.pins) {
        if (p.placedBatchAt === batchAt) {
          toDelete.push({ userId: m.userId, regionId: m.regionId, pinId: p.id });
        }
      }
    }
    if (toDelete.length === 0) return;
    setEditing({ ...editing, phase: "removing" });
    setError(null);
    try {
      await removePinBatch(toDelete);
      setEditing(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setEditing((prev) => (prev ? { ...prev, phase: "viewing" } : prev));
    }
  }

  /** Single onPlace handler: routes the lat/lng based on current mode.
   *   - awaiting-new-location → store as pendingMove + go to confirming
   *   - otherwise (no editing) → set `pending` for new-pin flow */
  function handlePlace(lat: number, lng: number) {
    // 點地圖 = 開始藏點動作 → 順手收起篩選面板,免得它擋住地圖。2026-05-30。
    setFilterOpen(false);
    if (editing && editing.phase === "awaiting-new-location") {
      setEditing({ ...editing, phase: "confirming-move", pendingMove: { lat, lng } });
      return;
    }
    if (editing) return; // any other edit phase ignores map taps
    setPending({ lat, lng });
    // Pan the tapped point up into the visible area so the PinForm sheet
    // (covers the lower ~half) doesn't hide the pending crosshair — the
    // worker needs to see exactly where they tapped. Previously only the
    // GPS「藏一個」flow set focusPoint; a manual map tap left the point
    // potentially behind the form. (2026-05-30)
    setFocusPoint({ lat, lng });
  }

  return (
    <div
      style={{
        position: "relative",
        height: "100dvh",
        width: "100%",
        background: T.paper,
        overflow: "hidden",
      }}
    >
      {/* Full-bleed map */}
      {!loading && region && (
        <div style={{ position: "absolute", inset: 0, zIndex: 1, display: "flex" }}>
          <FieldMap
            regionId={region.id}
            regionCenterLat={region.center[0]}
            regionCenterLng={region.center[1]}
            regionZoom={region.zoom}
            regionBoundsSwLat={region.boundsSw[0]}
            regionBoundsSwLng={region.boundsSw[1]}
            regionBoundsNeLat={region.boundsNe[0]}
            regionBoundsNeLng={region.boundsNe[1]}
            markers={regionFieldMarkers}
            users={users}
            learnerDots={learnerDots}
            mode="admin"
            stadiaApiKey={env.stadiaApiKey}
            gps={geo.coords}
            gpsAccuracy={geo.accuracy}
            heading={heading}
            focusPoint={focusPoint}
            // Show the in-flight pending pin for either a new-pin flow OR
            // a "confirming-move" flow — both have a tentative crosshair
            // the worker can re-tap to relocate before confirming.
            pendingPin={
              pending ??
              (editing && editing.phase === "confirming-move"
                ? editing.pendingMove
                : null)
            }
            onPlace={handlePlace}
            onTapMarker={(m) => handleTapMarker(m.id)}
            // Worker taps "回到我的位置" → switch the active region to
            // whichever region contains their GPS (full set, not just open),
            // so the marker actually paints in-viewport instead of getting
            // clipped miles outside the current region's maxBounds. Matches
            // handlePinHere: workers may pre-place in not-yet-open regions.
            onRecenterTap={() => {
              // iOS gates DeviceOrientation behind a user gesture — this tap
              // is it. Request compass permission so the worker sees their
              // facing cone (unless already denied; iOS won't re-prompt).
              if (headingPermission !== "denied") {
                requestHeadingPermission().catch(() => {});
              }
              if (!geo.coords) return;
              const inside = regions.find((r) => {
                const lat = geo.coords!.lat;
                const lng = geo.coords!.lng;
                return (
                  lat >= r.boundsSw[0] &&
                  lat <= r.boundsNe[0] &&
                  lng >= r.boundsSw[1] &&
                  lng <= r.boundsNe[1]
                );
              });
              if (inside && inside.id !== region?.id) {
                setActiveRegionId(inside.id);
              }
            }}
          />
        </div>
      )}

      {loading && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "grid",
            placeItems: "center",
            zIndex: 2,
          }}
        >
          <Han size={12} color={T.ink350}>連線中…</Han>
        </div>
      )}

      {/* Top-left: region chip strip (floating) */}
      <div
        style={{
          position: "absolute",
          top: "calc(env(safe-area-inset-top, 0px) + 12px)",
          left: 12,
          right: 12,
          zIndex: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
        }}
      >
        {/* Region picker — same pattern as learner side: a paper badge
            with a transparent native <select> stretched over the whole
            box, so any tap opens the system picker. Saves the horizontal
            space the chip strip was eating. Closed regions appear in the
            list but are flagged with · 關 and disabled. */}
        <div
          style={{
            position: "relative",
            padding: "8px 12px",
            background: T.paperVeil,
            border: `1px solid ${T.ink100}`,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            display: "flex",
            alignItems: "baseline",
            gap: 8,
            minHeight: 44,
          }}
        >
          <MonoLabel size={11} color={T.ink500}>區 域</MonoLabel>
          <Han
            size={13}
            color={T.ink900}
            style={{ borderBottom: `1px dashed ${T.ink200}` }}
          >
            {region?.name ?? "(未選)"}
            <span style={{ marginLeft: 4, color: T.ink500 }}>▾</span>
          </Han>
          <select
            value={region?.id ?? ""}
            onChange={(e) => {
              setActiveRegionId(e.target.value);
              setPending(null);
              setEditing(null);
            }}
            aria-label="切換區域"
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0,
              width: "100%",
              height: "100%",
              cursor: "pointer",
              appearance: "none",
              WebkitAppearance: "none",
              border: "none",
              background: "transparent",
              fontSize: 16,
            }}
          >
            {regions.map((r) => (
              // 工作員端「關閉」只控制學員端可見性、不該擋工作員自己看。
              // 即便關閉、工作員仍要能切過去查/編 pin、視覺上標 · 關
              // 提示「目前學員看不到」。
              <option key={r.id} value={r.id}>
                {r.name}
                {r.open ? "" : " · 關"}
              </option>
            ))}
          </select>
        </div>
        {/* Learner filter — paper badge with "學員 · N/15" summary.
            Tapping opens a multi-select panel anchored below it. */}
        <div style={{ position: "relative" }}>
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            style={{
              padding: "8px 12px",
              background: T.paperVeil,
              border: `1px solid ${filterOpen ? T.ink900 : T.ink100}`,
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              minHeight: 44,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
            aria-expanded={filterOpen}
            aria-label="篩選學員"
          >
            <MonoLabel size={11} color={T.ink500}>學 員</MonoLabel>
            <Han
              size={13}
              color={T.ink900}
              style={{ borderBottom: `1px dashed ${T.ink200}` }}
            >
              {filterCount === cohortFilteredLearners.length ? "全部" : `${filterCount}/${cohortFilteredLearners.length}`}
              <span style={{ marginLeft: 4, color: T.ink500 }}>▾</span>
            </Han>
          </button>
          {filterOpen && (
            <div
              role="dialog"
              style={{
                position: "absolute",
                top: "calc(100% + 6px)",
                right: 0,
                minWidth: 220,
                maxHeight: "60vh",
                overflowY: "auto",
                background: T.paperSink,
                border: `1px solid ${T.ink200}`,
                backdropFilter: "blur(10px)",
                WebkitBackdropFilter: "blur(10px)",
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                zIndex: 4,
              }}
            >
              {/* 關閉 X — 右上角 44×44 tap target。工作員看完篩選想收起時
                  不用回頭再點上面的 badge。2026-05-30。 */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <MonoLabel size={11} color={T.ink500}>· 篩 選</MonoLabel>
                <button
                  type="button"
                  onClick={() => setFilterOpen(false)}
                  aria-label="關閉篩選"
                  style={{
                    background: "transparent",
                    border: "none",
                    padding: 0,
                    margin: -10,
                    cursor: "pointer",
                    fontFamily: T.mono,
                    fontSize: 22,
                    color: T.ink700,
                    lineHeight: 1,
                    width: 44,
                    height: 44,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    touchAction: "manipulation",
                  }}
                >
                  ×
                </button>
              </div>
              {/* 梯次過濾 — 只在資料庫出現 2+ 屆時才顯示。切換梯次時重設學員勾選。 */}
              {availableCohorts.length > 1 && (
                <>
                  <MonoLabel size={11} color={T.ink500}>· 梯 次</MonoLabel>
                  {[{ id: "all", label: "全 部 屆" }, ...availableCohorts.map((c) => ({ id: c, label: `${c} 屆` }))].map(({ id, label }) => (
                    <label
                      key={id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "6px 4px",
                        cursor: "pointer",
                        minHeight: 36,
                        fontFamily: T.han,
                        fontSize: 13,
                        color: T.ink900,
                      }}
                    >
                      <input
                        type="radio"
                        name="cohort-scope"
                        checked={mapCohortFilter === id}
                        onChange={() => {
                          setMapCohortFilter(id);
                          setSelectedUserIds(null);
                        }}
                        style={{ width: 16, height: 16, margin: 0, accentColor: "#0E0E0E" }}
                      />
                      {label}
                      {id !== "all" && id === currentCohortId && (
                        <span style={{ color: T.ink500, fontSize: 11, marginLeft: 2 }}>· 本屆</span>
                      )}
                    </label>
                  ))}
                  <div style={{ height: 1, background: T.ink100 }} />
                </>
              )}
              {/* 狀態過濾 — 找到 / 未找到。兩個都可勾、預設都開、不能都關。 */}
              <MonoLabel size={11} color={T.ink500}>· 狀 態</MonoLabel>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 4px",
                  cursor: "pointer",
                  minHeight: 36,
                  fontFamily: T.han,
                  fontSize: 13,
                  color: T.ink900,
                }}
              >
                <input
                  type="checkbox"
                  checked={showUnfound}
                  onChange={(e) => toggleShowUnfound(e.target.checked)}
                  style={{ width: 16, height: 16, margin: 0, accentColor: "#0E0E0E" }}
                />
                未 找 到
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 4px",
                  cursor: "pointer",
                  minHeight: 36,
                  fontFamily: T.han,
                  fontSize: 13,
                  color: T.ink900,
                }}
              >
                <input
                  type="checkbox"
                  checked={showFound}
                  onChange={(e) => toggleShowFound(e.target.checked)}
                  style={{ width: 16, height: 16, margin: 0, accentColor: "#0E0E0E" }}
                />
                已 找 到
              </label>
              <div style={{ height: 1, background: T.ink100 }} />
              {/* Placer 過濾 — 我自己 / 全部。預設「顯示我標記的」,
                  避免多人同時藏 pin 時地圖太亂。上組 mono label 把
                  radio 跟下方學員 checkbox 視覺分開、不再像同層。 */}
              <MonoLabel size={11} color={T.ink500}>· 誰 放 的</MonoLabel>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 4px",
                  cursor: "pointer",
                  minHeight: 36,
                  fontFamily: T.han,
                  fontSize: 13,
                  color: T.ink900,
                }}
              >
                <input
                  type="radio"
                  name="placer-scope"
                  checked={!showAllPlacers}
                  onChange={() => {
                    setShowAllPlacers(false);
                    setFilterOpen(false);
                  }}
                  style={{
                    width: 16,
                    height: 16,
                    margin: 0,
                    accentColor: "#0E0E0E",
                  }}
                />
                我 標 記 的
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "6px 4px",
                  cursor: "pointer",
                  minHeight: 36,
                  fontFamily: T.han,
                  fontSize: 13,
                  color: T.ink900,
                }}
              >
                <input
                  type="radio"
                  name="placer-scope"
                  checked={showAllPlacers}
                  onChange={() => {
                    setShowAllPlacers(true);
                    setFilterOpen(false);
                  }}
                  style={{
                    width: 16,
                    height: 16,
                    margin: 0,
                    accentColor: "#0E0E0E",
                  }}
                />
                全 部 標 記
              </label>
              <div style={{ height: 1, background: T.ink100 }} />
              {/* 學員 checkbox 分組:標題 + 全選 / 清除 兩顆 mono 按鈕。
                  - · 全 選 → setSelectedUserIds(null) → 14 人 pin 全顯示
                              (audit 全場用)
                  - · 清 除 → setSelectedUserIds(new Set()) → 0 人 pin
                              (從零挑特定幾個的起點、地圖暫時空白)
                  兩個動作意圖不同,所以給兩顆按鈕、各自 disable 在
                  「已經是該狀態」時。標題只留「學 員」、字級拉到跟下方
                  學員名字一致(13/han),整排才塞得下又跟 list 視覺對齊。 */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "2px 4px",
                  minHeight: 24,
                }}
              >
                <span style={{ fontFamily: T.han, fontSize: 13, color: T.ink500 }}>
                  學 員
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button
                    type="button"
                    onClick={() => setSelectedUserIds(null)}
                    disabled={selectedUserIds === null}
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: "4px 6px",
                      margin: 0,
                      cursor:
                        selectedUserIds === null ? "default" : "pointer",
                      fontFamily: T.han,
                      fontSize: 13,
                      color:
                        selectedUserIds === null ? T.ink200 : T.ink700,
                    }}
                    aria-label="全選學員"
                  >
                    · 全 選
                  </button>
                  <span style={{ color: T.ink200, fontFamily: T.han, fontSize: 13 }}>|</span>
                  <button
                    type="button"
                    onClick={() => setSelectedUserIds(new Set())}
                    disabled={
                      selectedUserIds !== null && selectedUserIds.size === 0
                    }
                    style={{
                      background: "transparent",
                      border: "none",
                      padding: "4px 6px",
                      margin: 0,
                      cursor:
                        selectedUserIds !== null && selectedUserIds.size === 0
                          ? "default"
                          : "pointer",
                      fontFamily: T.han,
                      fontSize: 13,
                      color:
                        selectedUserIds !== null && selectedUserIds.size === 0
                          ? T.ink200
                          : T.ink700,
                    }}
                    aria-label="清除學員勾選"
                  >
                    · 清 除
                  </button>
                </div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {cohortFilteredLearners.map((u) => {
                  const checked =
                    selectedUserIds === null || selectedUserIds.has(u.id);
                  return (
                    <label
                      key={u.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "8px 4px",
                        cursor: "pointer",
                        minHeight: 36,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleUser(u.id)}
                        style={{
                          width: 16,
                          height: 16,
                          margin: 0,
                          accentColor: "#0E0E0E",
                        }}
                      />
                      <span
                        style={{
                          fontFamily: T.mono,
                          fontSize: 11,
                          color: T.ink500,
                          minWidth: 22,
                        }}
                      >
                        {u.no}
                      </span>
                      <span style={{ fontFamily: T.han, fontSize: 13, color: T.ink900 }}>
                        {u.name}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Bottom panel — docks above AdminNav (~125px on iOS).
          idle 態永遠有主按鈕「· 藏 一 個」、所以 idle 自身就算 content。
          其他模式(pending / editing / writing / error / 空集合 hint)
          照舊。helpDismissed 現在只藏「· 學員 40m」那一行、不影響 panel
          整體 render。 */}
      {(() => {
        const showEmptyHint =
          selectedUserIds !== null && selectedUserIds.size === 0;
        const isIdle = !pending && !writing && !editing;
        const hasContent =
          !!error ||
          writing ||
          !!pending ||
          !!editing ||
          isIdle ||
          showEmptyHint;
        if (!hasContent) return null;
        return (
      <div
        style={{
          position: "absolute",
          left: 12,
          right: 12,
          // 2026-05-30:從 140px 降到貼著底部 tab(AdminNav 內容高 ≈ 64px),
          // 把多出來的空間還給地圖。+4px 不完全壓住 tab 上緣的 hairline。
          bottom: "calc(68px + env(safe-area-inset-bottom, 0px))",
          zIndex: 3,
          background: T.paperVeil,
          border: `1px solid ${T.ink100}`,
          backdropFilter: "blur(8px)",
          WebkitBackdropFilter: "blur(8px)",
          padding: "12px 16px",
          maxHeight: "55vh",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        {error && (
          <Han size={11} color={T.ink900} lh={1.5}>
            · 寫入失敗:{error}
          </Han>
        )}
        {writing && (
          <Han size={12} color={T.ink500}>{writingPhase ?? "寫入中…"}</Han>
        )}
        {!pending && !writing && !editing && (
          <>
            {/* 2026-05-30:主動作按鈕「· 在此處標記」。重新設計成全寬、ink-900
                實心反白(這是放點的主入口、值得醒目、好按),disabled 走灰底灰字。
                文案從「· 藏 一 個」改「· 在此處標記」。 */}
            <button
              type="button"
              onClick={handlePinHere}
              disabled={pinHereDisabled}
              style={{
                alignSelf: "stretch",
                width: "100%",
                padding: "14px 20px",
                background: pinHereDisabled ? T.paperSink : T.ink900,
                border: `1px solid ${pinHereDisabled ? T.ink200 : T.ink900}`,
                color: pinHereDisabled ? T.ink350 : T.paper,
                fontFamily: T.han,
                fontSize: 16,
                letterSpacing: "0.3em",
                cursor: pinHereDisabled ? "default" : "pointer",
                minHeight: 50,
                borderRadius: 0,
                touchAction: "manipulation",
              }}
            >
              · 在 我 的 位 置 標 記
            </button>

            {/* 即時操作回饋保留(不是「說明」,是當下狀態/錯誤,工作員需要)。 */}
            {pinHereStatusHint && (
              <MonoLabel size={11} color={T.ink500}>{pinHereStatusHint}</MonoLabel>
            )}
            {pinHereWarning && (
              <MonoLabel size={11} color={T.ink700}>{pinHereWarning}</MonoLabel>
            )}
            {pinHereError && (
              <Han size={11} color={T.ink900} lh={1.6}>· {pinHereError}</Han>
            )}
          </>
        )}
        {pending && !writing && (
          <PinForm
            pending={pending}
            regionId={region.id}
            users={cohortFilteredLearners}
            regionMarkers={regionMarkerParents}
            onConfirm={handleConfirm}
            onCancel={() => {
              setPending(null);
              setFocusPoint(null);
            }}
          />
        )}
        {editing && editingMarker && !writing && (() => {
          // 算同批 pin 數:當下被點的 pin 有 placedBatchAt → 跨 markers
          // 統計同 batchAt 的總數。若沒此欄位、batchSize=1、panel 不顯示
          // 「· 移 除 同 批」按鈕。
          const targetPin = editingMarker.pins.find((p) => p.id === editing.pinId);
          const batchAt = targetPin?.placedBatchAt;
          let batchSize = 1;
          if (batchAt) {
            batchSize = 0;
            for (const m of regionMarkerParents) {
              for (const p of m.pins) {
                if (p.placedBatchAt === batchAt) batchSize += 1;
              }
            }
          }
          return (
            <PinEditPanel
              marker={editingMarker}
              pinId={editing.pinId}
              users={users}
              phase={editing.phase}
              pendingMove={editing.pendingMove}
              batchSize={batchSize}
              canToggleReward={REWARD_REGION_IDS.has(region.id)}
              onStartMove={handleStartMove}
              onConfirmMove={handleConfirmMove}
              onRemove={handleRemovePin}
              onRemoveBatch={handleRemoveBatch}
              onToggleReward={handleToggleReward}
              onAddPhotos={handleAddPhotos}
              onRemovePhoto={handleRemovePhoto}
              onClose={() => setEditing(null)}
            />
          );
        })()}
        {editing && !editingMarker && !writing && (
          // Marker disappeared from the snapshot mid-edit (another worker
          // deleted it, region was switched). Close gracefully.
          <Han size={11} color={T.ink500}>· 標記已不存在</Han>
        )}
      </div>
        );
      })()}

      {/* 2026-05-30:左下角「顯示學員」開關。開 → 地圖疊出有回報位置的學員
          灰點 + 姓名。預設關。放在 bottom panel 同層(zIndex 3)、左下,不撞
          region picker(top-left)/ zoom 鈕(top-right)/ AdminNav。 */}
      {!loading && region && (
        <button
          type="button"
          onClick={() => setShowLearners((v) => !v)}
          style={{
            position: "absolute",
            // 2026-05-30:原本放 bottom:68px 跟「藏一個」bottom panel 同一條
            // 底線、被全寬 panel 蓋住。改放左上角、region picker 那一排下方
            // (top + 60px)的地圖空白區,不被任何 bottom panel 擋。
            left: 12,
            top: "calc(env(safe-area-inset-top, 0px) + 64px)",
            zIndex: 4,
            padding: "8px 14px",
            minHeight: 40,
            background: showLearners ? T.ink900 : T.paperVeil,
            border: `1px solid ${showLearners ? T.ink900 : T.ink100}`,
            color: showLearners ? T.paper : T.ink700,
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            borderRadius: 0,
            cursor: "pointer",
            fontFamily: T.han,
            fontSize: 13,
            letterSpacing: "0.15em",
            touchAction: "manipulation",
          }}
        >
          {showLearners ? "隱 藏 學 員" : "顯 示 學 員"}
        </button>
      )}

      {/* 2026-05-30:一鍵「隱藏標記 / 顯示標記」總開關,疊在「顯示學員」下方。
          共用 selectedUserIds:空集合 = 全隱、null = 全顯。跟右上篩選面板的
          清除/全選同一個 state,不衝突。讓工作員不用點開面板就能一鍵清空。 */}
      {!loading && region && (() => {
        const allHidden =
          selectedUserIds !== null && selectedUserIds.size === 0;
        return (
          <button
            type="button"
            onClick={() =>
              setSelectedUserIds(allHidden ? null : new Set())
            }
            style={{
              position: "absolute",
              left: 12,
              top: "calc(env(safe-area-inset-top, 0px) + 112px)",
              zIndex: 4,
              padding: "8px 14px",
              minHeight: 40,
              background: allHidden ? T.ink900 : T.paperVeil,
              border: `1px solid ${allHidden ? T.ink900 : T.ink100}`,
              color: allHidden ? T.paper : T.ink700,
              backdropFilter: "blur(6px)",
              WebkitBackdropFilter: "blur(6px)",
              borderRadius: 0,
              cursor: "pointer",
              fontFamily: T.han,
              fontSize: 13,
              letterSpacing: "0.15em",
              touchAction: "manipulation",
            }}
          >
            {allHidden ? "顯 示 標 記" : "隱 藏 標 記"}
          </button>
        );
      })()}

    </div>
  );
}
